import {
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { INfeTotvsLogData } from "../../../../models/nfe-totvs-log";
import MainApi from "../../../../services/mainApi.service";
import { restartNfeTotvsRetries } from "../../../../services/nfeTotvs.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import NfeTotvsDetailsModal from "./NfeTotvsDetailsModal";

const NFE_TOTVS_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/ForubusNfeTotvs/ForubusNfeTotvs";

const nfeTotvsLogService = new MainApi<INfeTotvsLogData>(NFE_TOTVS_ROUTE);

const translationPath = "page.4uBus.logs.nfeTotvs.";

const NfeTotvs = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<INfeTotvsLogData | null>(null);

  const { sendRequest: restartNfeTotvsRetriesReq } = useFetch(
    restartNfeTotvsRetries,
  );

  const today = useMemo(() => new Date(), []);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Identifier: "",
    InvoiceIdentifier: "",
    StatusItem: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<INfeTotvsLogData>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identificador",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "invoiceIdentifier",
      headerName:
        t(translationPath + "invoice_identifier") || "Identificador da NFe",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "integrationType",
      headerName:
        t(translationPath + "integration_type") || "Tipo de Integração",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const row = params.row;
        const isCancel =
          row.invoiceDateCancellation !== null &&
          row.invoiceDateCancellation !== "";
        return (
          <Chip
            label={
              isCancel
                ? t(translationPath + "cancellation") || "Cancelamento"
                : t(translationPath + "integration") || "Integração"
            }
            color={isCancel ? "error" : "info"}
            size="small"
          />
        );
      },
    },
    {
      field: "statusItem",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          const configs: Record<
            string,
            { color: "warning" | "info" | "success" | "error"; key: string }
          > = {
            P: { color: "warning", key: "pending" },
            PENDENTE: { color: "warning", key: "pending" },
            PR: { color: "info", key: "processing" },
            PROCESSANDO: { color: "info", key: "processing" },
            F: { color: "success", key: "finished" },
            FINALIZADO: { color: "success", key: "finished" },
            E: { color: "error", key: "error" },
            ERRO: { color: "error", key: "error" },
          };

          return (
            configs[status] || { color: "warning" as const, key: "pending" }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },

    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "integratedDate",
      headerName:
        t(translationPath + "integrated_date") || "Data de Integração",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const dateStr = params.value.toString();
        if (
          dateStr.startsWith("0001-01-01") ||
          dateStr === "0001-01-01T00:00:00"
        ) {
          return "-";
        }

        const date = new Date(params.value);
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "-";
        }

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "totvsInvoiceIdentifier",
      headerName:
        t(translationPath + "totvsIdentifier") || "Identificador do totvs",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRestartNfeTotvsRetries = useCallback(
    async (identifier: number) => {
      const { success } = await restartNfeTotvsRetriesReq({
        identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(`${translationPath}resetedSuccess`),
          type: "success",
        });
      }
    },
    [onRefreshHandler, restartNfeTotvsRetriesReq, setMessage],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <NfeTotvsDetailsModal
            nfeData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Table<INfeTotvsLogData>
        title={t("components.menu.4ubus.logs.nfeTotvs")}
        columns={columns}
        service={nfeTotvsLogService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          params.row.statusItem === "ERRO"
            ? {
                action: () => {
                  handleRestartNfeTotvsRetries(params.row.identifier);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Reiniciar tentativas",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default NfeTotvs;
